import plur from 'plur';
import { formatCentsToUsd } from '@/utils/currency';
import { formatNumber } from '@/utils/number';
import { FLEX_FUNDS } from './flex-funds';

export const ADD_FUNDS_PRESETS_DOLLARS = [10, 25, 50, 100] as const;
const CUSTOM_AMOUNT_MIN_DOLLARS = FLEX_FUNDS.minCents / 100;
const CUSTOM_AMOUNT_MAX_DOLLARS = FLEX_FUNDS.maxCents / 100;
export const CUSTOM_AMOUNT_RANGE = `${formatCentsToUsd(FLEX_FUNDS.minCents, 0)} to ${formatCentsToUsd(FLEX_FUNDS.maxCents, 0)}`;

export const AUTO_RELOAD_DESCRIPTION = 'Refill your balance automatically when it runs low, so sending never stops.';

export const AUTO_RELOAD_MIN_TRIGGER_DOLLARS = 5;
export const AUTO_RELOAD_MIN_REPLENISH_DOLLARS = 10;
export const AUTO_RELOAD_MAX_REPLENISH_DOLLARS = 100;
const AUTO_RELOAD_MIN_REFILL_DOLLARS = 5;
const AUTO_RELOAD_MAX_PERIOD_CAP_DOLLARS = 1000;

export type AutoReloadSettings = {
  enabled: boolean;
  threshold_usd: number | null;
  target_usd: number | null;
  period_cap_enabled: boolean;
  period_cap_usd: number | null;
};

export const DEFAULT_AUTO_RELOAD_SETTINGS: AutoReloadSettings = {
  enabled: false,
  threshold_usd: 10,
  target_usd: 50,
  period_cap_enabled: true,
  period_cap_usd: 200,
};

export type AutoReloadErrors = {
  threshold_usd?: string;
  target_usd?: string;
  period_cap_usd?: string;
};

export type AmountSelection = { kind: 'preset'; dollars: number } | { kind: 'custom'; dollars: number | null };

export type MeteredSendChannel = 'email' | 'webhook';

export type AddFundsFunding =
  | { kind: 'send'; name: string; needCents: number; channel: MeteredSendChannel; messages: number }
  | { kind: 'automation'; name: string; needCents: number }
  | { kind: 'import'; name: string; needCents: number; newProfiles: number };

export function fundingTitle(funding: AddFundsFunding): string {
  switch (funding.kind) {
    case 'send':
      return `Add funds to send ${funding.name}`;
    case 'automation':
      return `Add funds to start ${funding.name}`;
    case 'import':
      return `Add funds to import ${funding.name}`;
    default: {
      const _exhaustive: never = funding;
      return String(_exhaustive);
    }
  }
}

export function fundingTag(funding: AddFundsFunding): string | null {
  switch (funding.kind) {
    case 'send':
      return 'Covers this send';
    case 'import':
      return 'Covers this import';
    case 'automation':
      return null;
    default: {
      const _exhaustive: never = funding;
      return String(_exhaustive);
    }
  }
}

export function coveringAmountDollars(needCents: number): number {
  const preset = ADD_FUNDS_PRESETS_DOLLARS.find((dollars) => dollars * 100 >= needCents);
  if (preset != null) return preset;
  return Math.min(CUSTOM_AMOUNT_MAX_DOLLARS, Math.ceil(needCents / 1000) * 10);
}

function selectionFor(dollars: number): AmountSelection {
  return ADD_FUNDS_PRESETS_DOLLARS.some((preset) => preset === dollars)
    ? { kind: 'preset', dollars }
    : { kind: 'custom', dollars };
}

export function initialAmountSelection(
  initialDollars: number | undefined,
  funding?: AddFundsFunding,
  startsOnCustomAmount = false,
): AmountSelection {
  if (startsOnCustomAmount) return { kind: 'custom', dollars: null };
  if (initialDollars != null) return selectionFor(initialDollars);
  if (funding != null) return selectionFor(coveringAmountDollars(funding.needCents));
  return selectionFor(ADD_FUNDS_PRESETS_DOLLARS[0]);
}

export function approximateCount(count: number): string {
  if (count >= 1000) return `${formatNumber(Math.round(count / 1000))}K`;
  return formatNumber(Math.round(count));
}

export type FundingOutcome =
  | { kind: 'covers'; extraMessages: string | null }
  | { kind: 'above-estimate' }
  | { kind: 'short'; pickDollars: number };

export type FundingSummary = {
  subject: string;
  verb: string;
  needCents: number;
  what: string;
  outcome: FundingOutcome | null;
};

function fundingSubject(funding: AddFundsFunding): Pick<FundingSummary, 'subject' | 'verb' | 'what'> {
  switch (funding.kind) {
    case 'send':
      return {
        subject: 'This send',
        verb: 'needs',
        what: `${formatNumber(funding.messages)} ${plur(funding.channel, funding.messages)}`,
      };
    case 'automation':
      return { subject: 'This automation', verb: 'will likely cost', what: 'an estimate for its first 30 days' };
    case 'import':
      return { subject: 'This import', verb: 'needs', what: `${formatNumber(funding.newProfiles)} new profiles` };
    default: {
      const _exhaustive: never = funding;
      return _exhaustive;
    }
  }
}

function extraMessages(funding: AddFundsFunding, amountCents: number): string | null {
  if (funding.kind !== 'send' || funding.messages <= 0 || funding.needCents <= 0) return null;
  const centsPerMessage = funding.needCents / funding.messages;
  const extra = (amountCents - funding.needCents) / centsPerMessage;
  return `${approximateCount(extra)} more ${plur(funding.channel, Math.round(extra))}`;
}

function fundingOutcome(funding: AddFundsFunding, amountCents: number): FundingOutcome | null {
  if (amountCents <= 0) return null;
  if (amountCents < funding.needCents) {
    return { kind: 'short', pickDollars: coveringAmountDollars(funding.needCents) };
  }
  if (funding.kind === 'automation') return { kind: 'above-estimate' };
  return { kind: 'covers', extraMessages: extraMessages(funding, amountCents) };
}

export function fundingSummary(funding: AddFundsFunding, amountCents: number): FundingSummary {
  return {
    ...fundingSubject(funding),
    needCents: funding.needCents,
    outcome: fundingOutcome(funding, amountCents),
  };
}

export function customAmountError(dollars: number | null): string | undefined {
  if (
    dollars == null ||
    !Number.isInteger(dollars) ||
    dollars < CUSTOM_AMOUNT_MIN_DOLLARS ||
    dollars > CUSTOM_AMOUNT_MAX_DOLLARS
  ) {
    return `Enter a whole amount from ${CUSTOM_AMOUNT_RANGE}.`;
  }
  return undefined;
}

export function refillCents(settings: AutoReloadSettings): number {
  return ((settings.target_usd ?? 0) - (settings.threshold_usd ?? 0)) * 100;
}

export function autoReloadErrors(settings: AutoReloadSettings): AutoReloadErrors {
  if (!settings.enabled) return {};

  const threshold = settings.threshold_usd;
  const target = settings.target_usd;
  const errors: AutoReloadErrors = {};

  if (threshold == null || threshold < AUTO_RELOAD_MIN_TRIGGER_DOLLARS) {
    errors.threshold_usd = 'Set this to at least $5.';
  }

  if (target == null || target < AUTO_RELOAD_MIN_REPLENISH_DOLLARS || target > AUTO_RELOAD_MAX_REPLENISH_DOLLARS) {
    errors.target_usd = 'Refill to an amount from $10 to $100.';
  } else if (threshold != null && target < threshold + AUTO_RELOAD_MIN_REFILL_DOLLARS) {
    errors.target_usd = `Refill to at least ${formatCentsToUsd((threshold + AUTO_RELOAD_MIN_REFILL_DOLLARS) * 100, 0)}.`;
  }

  const refill = refillCents(settings);
  const periodCapDollars = settings.period_cap_usd ?? 0;
  if (settings.period_cap_enabled && periodCapDollars > AUTO_RELOAD_MAX_PERIOD_CAP_DOLLARS) {
    errors.period_cap_usd = `Limit cannot be more than ${formatCentsToUsd(AUTO_RELOAD_MAX_PERIOD_CAP_DOLLARS * 100, 0)}`;
  } else if (
    settings.period_cap_enabled &&
    errors.threshold_usd == null &&
    errors.target_usd == null &&
    periodCapDollars * 100 < refill
  ) {
    errors.period_cap_usd = `Set the limit to at least ${formatCentsToUsd(refill, 0)}, enough for one refill.`;
  }

  return errors;
}

export function hasAutoReloadErrors(errors: AutoReloadErrors): boolean {
  return errors.threshold_usd != null || errors.target_usd != null || errors.period_cap_usd != null;
}
